import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { get } from 'lodash';
import innerAnalyticsVolia from 'helpers/innerAnalyticsVolia';
import useModals from 'hooks/useModals';
import { MISSING_SUBSCRIPTION } from 'constants/visibility-types';
import { getIsLifecellFlag, getIsLoggedInSelector } from 'reducers/profile/selectors';
import { selectIsParentalControlEnabled, selectParentalControlAge } from 'reducers/parental-lock/selectors';
import PLAYER_TYPES from 'constants/player-types';
import isPgPinNeeded from 'utils/is-pg-pin-needed';
import { navigateToPlayerPage } from 'actions/player';
import { resolvePlayerNavLink } from 'helpers/urlhelper';
import { navigateToAuthPage } from 'actions/app';

const useCatchUpNavigation = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(getIsLoggedInSelector);
  const isParentalEnabled = useSelector(selectIsParentalControlEnabled);
  const isLifecellUser = useSelector(getIsLifecellFlag);
  const age = useSelector(selectParentalControlAge);
  const { showEnterPinModal, showLifecellPaymentModal } = useModals();

  const onEnter = useCallback(
    (item, modalFocusBackConfig, analyticsParams) => {
      const channelId = get(item.epgEvent, ['channel', 'id']) || item.channelId;
      const startTime = get(item.epgEvent, ['startTime']) || item.eventStartTime;
      const payload = {
        id: channelId,
        metadata: { startTime },
      };
      const epgEventState = item.epgEvent?.state ?? item.epgEventState;

      // SHould go to live if it's future program
      if (epgEventState === 'FUTURE') delete payload.metadata;

      const goToPlayer = () => {
        // Conveted to channel structure because of inner checks
        dispatch(
          navigateToPlayerPage({
            item: payload,
            type: PLAYER_TYPES.CHANNEL,
          }),
        );
      };

      const isVodChannel = !!item.epgEvent?.channel?.isVod;
      const addInfo = `vod_asset_id=${item.id};channel_id=${channelId};content_type=EPG_EVENT;type=VOD_ASSET;epgEventState=${epgEventState}`;

      if (analyticsParams) innerAnalyticsVolia.postEvent({ ...analyticsParams, addInfo });

      if (!isLoggedIn) {
        const onSuccess = resolvePlayerNavLink(payload, {
          type: PLAYER_TYPES.CHANNEL,
          isVodChannel,
        });
        const search = `?channelId=${channelId}&onSuccess=${onSuccess}`;
        dispatch(navigateToAuthPage(search));
        return;
      }
      if (item.visibilityDetails === MISSING_SUBSCRIPTION) {
        const onSuccess = resolvePlayerNavLink(payload, {
          type: PLAYER_TYPES.CHANNEL,
          isVodChannel,
        });

        if (isLifecellUser) {
          showLifecellPaymentModal(
            {
              id: channelId,
              assetType: 'channelId',
              onSuccess: goToPlayer,
            },
            modalFocusBackConfig,
          );
          return;
        }
        return history.push({
          pathname: '/profile/packages/filter',
          search: `?channelId=${channelId}&onSuccess=${onSuccess}`,
        });
      }
      if (isParentalEnabled && isPgPinNeeded(item.pgRating, age)) {
        return showEnterPinModal(modalFocusBackConfig, goToPlayer);
      }

      goToPlayer();
    },
    [
      age,
      dispatch,
      history,
      isLifecellUser,
      isLoggedIn,
      isParentalEnabled,
      showEnterPinModal,
      showLifecellPaymentModal,
    ],
  );

  return onEnter;
};

export default useCatchUpNavigation;
